import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { InteractionObjectType, InteractionType } from "../../constants";
import { InteractionTagData } from "../../models";
import { addOrRemoveObjectInteractions, createInteraction } from "../../service/interaction";
import { searchInteractions } from "../../service/query";
import { ServiceError, Status } from "../../service/Shared";
import { useAuth } from "../../useAuth";
import { dataGridMapFilterCriteria } from "../../widget";

// A tag definition (org-scoped) plus whether it is currently linked to the open task.
export interface TaskTag {
  uuid: string;
  name: string;
  checked: boolean;
  rowVersion: number;
  // Tags typed into the add-control that do not yet exist against the organisation.
  isNew?: boolean;
}

interface UseTaskTagsReturnData {
  tags: TaskTag[];
  linkedTags: TaskTag[];
  availableTags: TaskTag[];
  newTagName: string;
  setNewTagName: (value: string) => void;

  handleToggleTag: (uuid: string) => void;
  handleRemoveTag: (uuid: string) => void;
  handleAddNewTag: () => void;

  // Two-phase link: called with the persisted task uuid (existing task on edit, or the
  // uuid returned by createWorkflowTask on create). Returns errors, if any.
  saveTags: (taskUuid: string) => Promise<ServiceError[] | undefined>;
}

export const useTaskTags = (taskUuid: string | undefined): UseTaskTagsReturnData => {
  const { currentOrganisationUuid } = useAuth();

  const [tags, setTags] = useState<TaskTag[]>([]);
  const [initialTags, setInitialTags] = useState<TaskTag[]>([]);
  const [newTagName, setNewTagName] = useState("");
  // Monotonic id of the latest load, so a response for a task the panel has moved on from is discarded.
  const requestIdRef = useRef(0);

  const fetchTags = useCallback(async () => {
    if (!currentOrganisationUuid) return;

    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    const filterCriteria = dataGridMapFilterCriteria([]);
    filterCriteria.type = { operator: "eq", value: InteractionType.TAG };
    filterCriteria.objectType = { operator: "eq", value: InteractionObjectType.ORGANISATION };
    filterCriteria.objectUuid = { operator: "eq", value: currentOrganisationUuid };

    const res = await searchInteractions({
      paging: { beforeCursor: null, afterCursor: null, limit: 50 },
      filter: { results: filterCriteria },
    });

    // Closing one task and opening another overlaps two loads; only the newest may apply its result,
    // otherwise the previous task's checked tags land here and become the baseline for tag edits.
    if (requestId !== requestIdRef.current) return;

    if (res.status === Status.Success && res.data?.results != null) {
      const mapped: TaskTag[] = res.data.results.map((interaction) => ({
        uuid: interaction.uuid,
        name: (JSON.parse(interaction.data) as InteractionTagData).name,
        checked:
          taskUuid != null &&
          interaction.objectInteractions.some((oi) => oi.objectUuid === taskUuid && oi.endDate == null),
        rowVersion: interaction.rowVersion,
      }));

      setTags(mapped);
      setInitialTags(mapped);
      setNewTagName("");
    }
  }, [currentOrganisationUuid, taskUuid]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  const handleToggleTag = (uuid: string): void => {
    setTags((prev) => prev.map((tag) => (tag.uuid === uuid ? { ...tag, checked: !tag.checked } : tag)));
  };

  const handleRemoveTag = (uuid: string): void => {
    setTags((prev) => prev.map((tag) => (tag.uuid === uuid ? { ...tag, checked: false } : tag)));
  };

  const handleAddNewTag = (): void => {
    const name = newTagName.trim();
    if (name.length === 0) return;

    setTags((prev) => {
      // Re-link an existing tag by name rather than creating a duplicate.
      const existing = prev.find((tag) => tag.name.toLowerCase() === name.toLowerCase());
      if (existing) {
        return prev.map((tag) => (tag.uuid === existing.uuid ? { ...tag, checked: true } : tag));
      }
      return [...prev, { uuid: `new-${name}`, name, checked: true, rowVersion: 1, isNew: true }];
    });
    setNewTagName("");
  };

  const saveTags = async (resolvedTaskUuid: string): Promise<ServiceError[] | undefined> => {
    // Create any brand-new, checked tags against the organisation first, capturing their uuids.
    const tagsToCreate = tags.filter((tag) => tag.isNew && tag.checked);
    const createdByName = new Map<string, string>();

    if (tagsToCreate.length > 0 && currentOrganisationUuid) {
      const createRes = await createInteraction({
        interactions: tagsToCreate.map((tag) => ({
          data: JSON.stringify({ name: tag.name } as InteractionTagData),
          objectType: InteractionObjectType.ORGANISATION,
          objectUuid: currentOrganisationUuid,
          type: InteractionType.TAG,
        })),
      });

      if (createRes.status === Status.Error) return createRes.errors;

      createRes.data?.uuids.forEach((uuid, index) => {
        createdByName.set(tagsToCreate[index].name, uuid);
      });
    }

    // Existing tags whose linked state changed, plus newly created checked tags.
    const initialByUuid = new Map(initialTags.map((tag) => [tag.uuid, tag]));
    const changedExisting = tags.filter(
      (tag) => !tag.isNew && tag.checked !== (initialByUuid.get(tag.uuid)?.checked ?? false)
    );

    const objectInteractions = [
      ...changedExisting.map((tag) => ({
        interactionUuid: tag.uuid,
        objectType: InteractionObjectType.WORKFLOW_TASK,
        objectUuid: resolvedTaskUuid,
        operation: tag.checked ? "add" : "remove",
      })),
      ...tagsToCreate.flatMap((tag) => {
        const uuid = createdByName.get(tag.name);
        return uuid
          ? [
              {
                interactionUuid: uuid,
                objectType: InteractionObjectType.WORKFLOW_TASK,
                objectUuid: resolvedTaskUuid,
                operation: "add",
              },
            ]
          : [];
      }),
    ];

    if (objectInteractions.length === 0) return undefined;

    const linkRes = await addOrRemoveObjectInteractions({ objectInteractions });
    return linkRes.status === Status.Error ? linkRes.errors : undefined;
  };

  const linkedTags = useMemo(() => tags.filter((tag) => tag.checked), [tags]);
  const availableTags = useMemo(() => tags.filter((tag) => !tag.checked && !tag.isNew), [tags]);

  return {
    tags,
    linkedTags,
    availableTags,
    newTagName,
    setNewTagName,
    handleToggleTag,
    handleRemoveTag,
    handleAddNewTag,
    saveTags,
  };
};
